import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Copy, Repeat, Plus, Trash2, CalendarDays } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { formatDateFr } from "../lib/dateFormat";
import ShiftConflictDialog from "./ShiftConflictDialog";

const WEEKDAYS = [
  { value: 0, label: "Lun" },
  { value: 1, label: "Mar" },
  { value: 2, label: "Mer" },
  { value: 3, label: "Jeu" },
  { value: 4, label: "Ven" },
  { value: 5, label: "Sam" },
  { value: 6, label: "Dim" },
];

/**
 * Modale Dupliquer / Récurrence pour un shift ou une intervention.
 *
 * Props :
 *  - kind: "shift" | "event"
 *  - source: { id, date, label?, start_time, end_time }
 *  - onSuccess(count): callback (rafraîchit la liste appelante)
 *
 * URLs appelées :
 *  - POST /api/shifts/{id}/duplicate            body: { dates: [iso] }
 *  - POST /api/shifts/{id}/recurrence           body: { frequency, end_date, weekdays? }
 *  - POST /api/department-agenda/{id}/duplicate body: { dates: [iso] }
 *  - POST /api/department-agenda/{id}/recurrence body: { frequency, end_date, weekdays? }
 */
export default function DuplicateRecurrenceDialog({
  open, onOpenChange, kind, source, onSuccess, onViewConflictShift,
}) {
  const [tab, setTab] = useState("duplicate");

  // Onglet "Dupliquer" : liste de dates ad-hoc
  const [dates, setDates] = useState([""]);

  // Onglet "Récurrence"
  const [frequency, setFrequency] = useState("weekly");
  const [endDate, setEndDate] = useState("");
  const [weekdays, setWeekdays] = useState([]);
  const [busy, setBusy] = useState(false);
  // Modale de confirmation anti-conflit (uniquement pour la duplication de shift)
  const [conflictDialog, setConflictDialog] = useState({ open: false, conflict: null });

  useEffect(() => {
    if (open) {
      setTab("duplicate");
      setDates([""]);
      setFrequency("weekly");
      setEndDate("");
      setWeekdays([]);
      setConflictDialog({ open: false, conflict: null });
    }
  }, [open, source?.id]);

  const baseUrl = kind === "shift"
    ? `/shifts/${source?.id}`
    : `/department-agenda/${source?.id}`;
  const label = kind === "shift" ? "shift" : "intervention";

  const doDuplicate = async (clean, force = false) => {
    const qs = force ? "?force=true" : "";
    const { data } = await api.post(`${baseUrl}/duplicate${qs}`, { dates: clean });
    toast.success(
      `${data.created} ${label}${data.created > 1 ? "s" : ""} dupliqué${data.created > 1 ? "s" : ""}` +
      (force ? " (conflit ignoré)" : "")
    );
    onOpenChange?.(false);
    onSuccess?.(data.created);
  };

  const submitDuplicate = async () => {
    const clean = dates.filter((d) => d).map((d) => d.trim());
    if (clean.length === 0) {
      toast.error("Ajoutez au moins une date");
      return;
    }
    if (clean.some((d) => d === source?.date)) {
      toast.error("Vous ne pouvez pas dupliquer sur la date d'origine du " + label);
      return;
    }
    setBusy(true);
    try {
      await doDuplicate(clean, false);
    } catch (e) {
      // Interception 409 : afficher la modale bloquante (uniquement pour shifts)
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (kind === "shift" && status === 409 && detail && typeof detail === "object" && detail.code === "shift_conflict") {
        setConflictDialog({ open: true, conflict: detail, pendingDates: clean });
      } else {
        toast.error(e?.response?.data?.detail || "Erreur lors de la duplication");
      }
    } finally {
      setBusy(false);
    }
  };

  const confirmDuplicateBypass = async () => {
    const pendingDates = conflictDialog.pendingDates || [];
    setBusy(true);
    try {
      await doDuplicate(pendingDates, true);
      setConflictDialog({ open: false, conflict: null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la confirmation");
      setConflictDialog({ open: false, conflict: null });
    } finally {
      setBusy(false);
    }
  };

  const submitRecurrence = async () => {
    if (!endDate) {
      toast.error("La date de fin est obligatoire");
      return;
    }
    if (endDate <= (source?.date || "")) {
      toast.error("La date de fin doit être postérieure au " + label);
      return;
    }
    if (frequency === "weekly" && weekdays.length === 0) {
      // OK : backend reprend le jour de la semaine du source
    }
    setBusy(true);
    try {
      const payload = { frequency, end_date: endDate };
      if (frequency === "weekly" && weekdays.length > 0) payload.weekdays = weekdays;
      const { data } = await api.post(`${baseUrl}/recurrence`, payload);
      toast.success(`${data.created} occurrence${data.created > 1 ? "s" : ""} créée${data.created > 1 ? "s" : ""}`);
      onOpenChange?.(false);
      onSuccess?.(data.created);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la programmation");
    } finally {
      setBusy(false);
    }
  };

  const toggleWeekday = (v) => {
    setWeekdays((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="dup-rec-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <CalendarDays size={18} /> Dupliquer / Programmer une récurrence
          </DialogTitle>
          <DialogDescription>
            <span className="text-zinc-700">Source : </span>
            <span className="font-mono text-zinc-900">
              {source?.label || "—"} — {source?.date ? formatDateFr(source.date) : "—"} ({source?.start_time}–{source?.end_time})
            </span>
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="duplicate" data-testid="dup-rec-tab-duplicate">
              <Copy size={13} className="mr-1.5" /> Dupliquer
            </TabsTrigger>
            <TabsTrigger value="recurrence" data-testid="dup-rec-tab-recurrence">
              <Repeat size={13} className="mr-1.5" /> Récurrence
            </TabsTrigger>
          </TabsList>

          <TabsContent value="duplicate" className="space-y-3 pt-3">
            <p className="text-xs text-zinc-600 font-mono">
              Sélectionnez une ou plusieurs dates pour dupliquer ce {label} à l&apos;identique.
            </p>
            <div className="space-y-2">
              {dates.map((d, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={d}
                    onChange={(e) => setDates((arr) => arr.map((x, i) => i === idx ? e.target.value : x))}
                    data-testid={`dup-date-${idx}`}
                  />
                  {dates.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setDates((arr) => arr.filter((_, i) => i !== idx))}
                      className="text-zinc-400 hover:text-red-600 p-1"
                      data-testid={`dup-date-remove-${idx}`}
                      aria-label="Retirer cette date"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => setDates((arr) => [...arr, ""])}
                className="inline-flex items-center gap-1.5 text-xs text-zinc-700 hover:text-zinc-900 px-2 py-1 rounded-sm border border-dashed border-zinc-300 hover:bg-zinc-50"
                data-testid="dup-date-add"
              >
                <Plus size={12} /> Ajouter une date
              </button>
            </div>
          </TabsContent>

          <TabsContent value="recurrence" className="space-y-3 pt-3">
            <p className="text-xs text-zinc-600 font-mono">
              Crée automatiquement des copies de ce {label} jusqu&apos;à la date de fin choisie.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="label-uppercase mb-1.5 block">Fréquence</Label>
                <Select value={frequency} onValueChange={setFrequency}>
                  <SelectTrigger data-testid="rec-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Tous les jours</SelectItem>
                    <SelectItem value="weekly">Chaque semaine</SelectItem>
                    <SelectItem value="monthly">Chaque mois</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-uppercase mb-1.5 block">
                  Date de fin <span className="text-red-600">*</span>
                </Label>
                <Input
                  type="date"
                  value={endDate}
                  min={source?.date || undefined}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="rec-end-date"
                />
              </div>
            </div>
            {frequency === "weekly" && (
              <div>
                <Label className="label-uppercase mb-1.5 block">Jours de la semaine (optionnel)</Label>
                <div className="flex flex-wrap gap-1.5">
                  {WEEKDAYS.map((w) => (
                    <button
                      key={w.value}
                      type="button"
                      onClick={() => toggleWeekday(w.value)}
                      className={`text-xs font-mono px-2.5 py-1 rounded-sm border ${
                        weekdays.includes(w.value)
                          ? "bg-zinc-900 text-white border-zinc-900"
                          : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                      }`}
                      data-testid={`rec-weekday-${w.value}`}
                    >
                      {w.label}
                    </button>
                  ))}
                </div>
                <p className="mt-1 text-[11px] text-zinc-500 font-mono">
                  Vide = même jour de la semaine que la source.
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2 mt-2">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={busy} data-testid="dup-rec-cancel">
            Annuler
          </Button>
          {tab === "duplicate" ? (
            <Button onClick={submitDuplicate} disabled={busy} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="dup-rec-submit-duplicate">
              {busy ? "…" : "Dupliquer"}
            </Button>
          ) : (
            <Button onClick={submitRecurrence} disabled={busy || !endDate} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="dup-rec-submit-recurrence">
              {busy ? "…" : "Programmer"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {/* Modale bloquante : conflit horaire / indisponibilité (uniquement pour la duplication de shift) */}
    <ShiftConflictDialog
      open={conflictDialog.open}
      onOpenChange={(v) => { if (!v) setConflictDialog({ open: false, conflict: null }); }}
      conflict={conflictDialog.conflict}
      onCancel={() => setConflictDialog({ open: false, conflict: null })}
      onConfirm={confirmDuplicateBypass}
      busy={busy}
      onViewShift={onViewConflictShift}
    />
    </>
  );
}
